import { Maybe } from '@/types/globals';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { RxProcedureTypeControlFacade } from './rx-procedure-type-control.facade';
import { ProcedureTypeService } from './procedure-type.service';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { IdName } from '@shared/models/id-name';
import { SharedModule } from '@shared/shared.module';
import { filter, map, Observable, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';

@Component({
	selector: 'rx-procedure-type-control',
	templateUrl: './rx-procedure-type-control.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [SharedModule, AsyncPipe],
	providers: [RxProcedureTypeControlFacade, ProcedureTypeService]
})
export class RxProcedureTypeControlComponent extends BaseDestroyableComponent implements OnInit {
	@HostBinding('style') @Input() styles: { [key: string]: string } = {};
	@Input() id = 'procedure-type-control';

	readonly procedureTypeControl = new FormControl<IdName | null>(null);

	readonly procedureTypes$: Observable<IdName[]> = this.facade.procedureTypes$;
	readonly selectedProcedureType$: Observable<Maybe<IdName>> = this.facade.procedureType$;
	readonly isProcedureAndTypeReadOnly$: Observable<boolean> = this.facade.isProcedureAndTypeReadOnly$;

	constructor(private readonly facade: RxProcedureTypeControlFacade) {
		super();
	}

	ngOnInit(): void {
		this.subscribeToSelectedProcedureType();
		this.subscribeToControlValueChanges();
	}

	private subscribeToSelectedProcedureType(): void {
		this.selectedProcedureType$
			.pipe(takeUntil(this.componentAlive$))
			.subscribe(type => this.procedureTypeControl.setValue(type ?? null, { emitEvent: false }));
	}

	private subscribeToControlValueChanges(): void {
		this.procedureTypeControl.valueChanges
			.pipe(
				withLatestFrom(this.selectedProcedureType$),
				filter(([next, current]) => next?.Id !== current?.Id),
				switchMap(([next, current]) =>
					this.facade
						.handleProcedureTypeChanged(next?.Id ?? 0)
						.pipe(map(isChangeAllowed => ({ isChangeAllowed, revertTo: current })))
				),
				filter(({ isChangeAllowed }) => !isChangeAllowed),
				tap(({ revertTo }) => this.procedureTypeControl.setValue(revertTo ?? null, { emitEvent: false })),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}
}
